/**
 * Per-account-type behavioral metadata. Centralizing this keeps the engine's
 * tax/withdrawal logic honest — the behavioral notes in spec §4 live here as
 * data rather than being scattered through conditionals.
 */
import type { AccountType, TaxFamily } from './types';

export interface AccountMeta {
  taxFamily: TaxFamily;
  /** Subject to Required Minimum Distributions during the owner's lifetime. */
  rmdEligible: boolean;
  /**
   * Withdrawals of earnings/gains are taxed as ordinary income (pre-tax) vs.
   * tax-free (roth/hsa-qualified) vs. capital-gains on the gain (aftertax).
   */
  withdrawalTax: 'ordinary' | 'tax_free' | 'capital_gains' | 'ordinary_after_65_hsa';
  /** 10% early-withdrawal penalty before 59.5 applies (with exceptions). */
  earlyPenaltyApplies: boolean;
  /**
   * 457(b) governmental plans have NO 10% early-withdrawal penalty after
   * separation from service — materially valuable to early retirees (spec §4).
   */
  noPenaltyAfterSeparation?: boolean;
  /**
   * Employer plans (401(k)/403(b)) can qualify for the "Rule of 55": separate
   * from service in or after the year you turn 55 and THAT employer's plan is
   * penalty-free immediately. IRAs never qualify, and rolling the plan into an
   * IRA forfeits it — so this is type-level eligibility only; the account also
   * has to be opted in (`currentEmployerPlan`).
   */
  rule55Possible?: boolean;
}

export const ACCOUNT_META: Record<AccountType, AccountMeta> = {
  trad_401k: { taxFamily: 'pretax', rmdEligible: true, withdrawalTax: 'ordinary', earlyPenaltyApplies: true, rule55Possible: true },
  trad_403b: { taxFamily: 'pretax', rmdEligible: true, withdrawalTax: 'ordinary', earlyPenaltyApplies: true, rule55Possible: true },
  trad_457b: {
    taxFamily: 'pretax',
    rmdEligible: true,
    withdrawalTax: 'ordinary',
    earlyPenaltyApplies: false,
    noPenaltyAfterSeparation: true,
  },
  trad_ira: { taxFamily: 'pretax', rmdEligible: true, withdrawalTax: 'ordinary', earlyPenaltyApplies: true },

  // SECURE 2.0 eliminated lifetime RMDs for designated Roth accounts in
  // employer plans starting 2024 — so no Roth type is RMD-eligible.
  roth_401k: { taxFamily: 'roth', rmdEligible: false, withdrawalTax: 'tax_free', earlyPenaltyApplies: true, rule55Possible: true },
  roth_403b: { taxFamily: 'roth', rmdEligible: false, withdrawalTax: 'tax_free', earlyPenaltyApplies: true, rule55Possible: true },
  roth_457b: { taxFamily: 'roth', rmdEligible: false, withdrawalTax: 'tax_free', earlyPenaltyApplies: false, noPenaltyAfterSeparation: true },
  roth_ira: { taxFamily: 'roth', rmdEligible: false, withdrawalTax: 'tax_free', earlyPenaltyApplies: true },

  // HSA: tax-free for qualified medical at any age; after 65 non-medical
  // withdrawals are ordinary income without penalty.
  hsa: { taxFamily: 'hsa', rmdEligible: false, withdrawalTax: 'ordinary_after_65_hsa', earlyPenaltyApplies: true },
  college_529: { taxFamily: 'education', rmdEligible: false, withdrawalTax: 'tax_free', earlyPenaltyApplies: false },

  inherited_ira_trad: { taxFamily: 'pretax', rmdEligible: true, withdrawalTax: 'ordinary', earlyPenaltyApplies: false },
  inherited_ira_roth: { taxFamily: 'roth', rmdEligible: false, withdrawalTax: 'tax_free', earlyPenaltyApplies: false },

  brokerage: { taxFamily: 'aftertax', rmdEligible: false, withdrawalTax: 'capital_gains', earlyPenaltyApplies: false },
  checking_savings: { taxFamily: 'aftertax', rmdEligible: false, withdrawalTax: 'capital_gains', earlyPenaltyApplies: false },
  cd_money_market: { taxFamily: 'aftertax', rmdEligible: false, withdrawalTax: 'capital_gains', earlyPenaltyApplies: false },
  bonds: { taxFamily: 'aftertax', rmdEligible: false, withdrawalTax: 'capital_gains', earlyPenaltyApplies: false },
};

/** Allocation a newly added account starts with, so the type implies the
 *  asset mix people expect (a bond holding earns the bond rate, not 60/35/5). */
export const DEFAULT_ALLOCATION: Partial<Record<AccountType, { equityPct: number; bondPct: number; cashPct: number }>> = {
  bonds: { equityPct: 0, bondPct: 100, cashPct: 0 },
  cd_money_market: { equityPct: 0, bondPct: 0, cashPct: 100 },
  checking_savings: { equityPct: 0, bondPct: 0, cashPct: 100 },
};

/** Human-friendly labels for the account picker. */
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  trad_401k: 'Traditional 401(k)',
  trad_403b: 'Traditional 403(b)',
  trad_457b: 'Governmental 457(b)',
  trad_ira: 'Traditional IRA',
  roth_401k: 'Roth 401(k)',
  roth_403b: 'Roth 403(b)',
  roth_457b: 'Roth 457(b)',
  roth_ira: 'Roth IRA',
  hsa: 'HSA',
  college_529: '529 College Savings',
  inherited_ira_trad: 'Inherited IRA (Traditional)',
  inherited_ira_roth: 'Inherited IRA (Roth)',
  brokerage: 'Taxable Brokerage',
  checking_savings: 'Checking / Savings',
  cd_money_market: 'CD / Money Market',
  bonds: 'Bonds / Treasuries (taxable)',
};

export function taxFamilyOf(type: AccountType): TaxFamily {
  return ACCOUNT_META[type].taxFamily;
}
